import { Component, type ErrorInfo, type ReactNode } from 'react'

import AppErrorFallback from './AppErrorFallback'

interface ErrorBoundaryFallbackProps {
  error: Error | null
  incidentRef: string | null
  onReset: () => void
}

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode | ((props: ErrorBoundaryFallbackProps) => ReactNode)
  onError?: (error: Error, info: ErrorInfo) => string | null | void
  onReset?: () => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  incidentRef: string | null
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null, incidentRef: null }
    this.handleReset = this.handleReset.bind(this)
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, incidentRef: null }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    const incidentRef = this.props.onError?.(error, info)
    if (incidentRef) {
      this.setState({ incidentRef })
    }
  }

  handleReset() {
    this.setState({ hasError: false, error: null, incidentRef: null })
    this.props.onReset?.()
  }

  override render() {
    const { hasError, error, incidentRef } = this.state
    const { fallback, children } = this.props

    if (!hasError) {
      return children
    }

    if (fallback) {
      if (typeof fallback === 'function') {
        return fallback({ error, incidentRef, onReset: this.handleReset })
      }
      return fallback
    }

    return <AppErrorFallback incidentRef={incidentRef} onReset={this.handleReset} />
  }
}
